import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ccoActions from "../../../../_actions/ccoActions";
import bidItemActions from "../../../../_actions/bidItemActions";

import locationActions from "../../../../_actions/locationActions";
// import LocationMultiselect from "../_common/LocationMultiselect";

const CCO_TYPES = {
  BID_ITEM: "bidItem",
  FORCE_ACCOUNT: "forceAccount",
  AGREED_PRICE: "agreedLumpSum",
};

const CCO_TYPE_OPTIONS = [
  {
    value: CCO_TYPES.BID_ITEM,
    label: "Bid Item at Bid Item Unit Price",
    description: "Adjust the quantity of one or more existing bid items, paid at the bid unit price.",
  },
  {
    value: CCO_TYPES.FORCE_ACCOUNT,
    label: "Extra Work at Force Account",
    description: "All labor, equipment, and materials are tracked to determine the amount to pay.",
  },
  {
    value: CCO_TYPES.AGREED_PRICE,
    label: "Extra Work at Agreed Price",
    description: "A fixed agreed price for extra work on this change order.",
  },
];

function detectCcoType(data) {
  if (!data) return CCO_TYPES.BID_ITEM;
  if (data.ccoType) return data.ccoType;
  if (data.ccoAgreedLumpSums?.length > 0) return CCO_TYPES.AGREED_PRICE;
  if (data.ccoExtraWorkForceAccounts?.length > 0) return CCO_TYPES.FORCE_ACCOUNT;
  return CCO_TYPES.BID_ITEM;
}

export default function EditCcoModalV2(props) {
  const { showModal, toggleModal, ccoId, projectId } = props;

  const dispatch = useDispatch();

  useEffect(() => dispatch(locationActions.getAll(projectId)), []);
  useEffect(() => dispatch(bidItemActions.getAll(projectId)), []);

  const projLocationList = useSelector(
    (state) => state.locations.projLocationLists[projectId] || null
  );

  const projItemList = useSelector(
    (state) => state.bidItems.projItemLists[projectId] || null
  );

  const ccoData = useSelector((state) =>
    state.ccos.openCcoDetails[projectId]
      ? state.ccos.openCcoDetails[projectId][ccoId] || null
      : null
  );

  const [_data, setData] = useState(ccoData ? { ...ccoData } : null);

  const [ccoNo, setCcoNo] = useState((_data && _data.ccoNo) || "");
  const [supplementNo, setSupplementNo] = useState((_data && _data.supplementNo) || "0");
  const [description, setDescription] = useState((_data && _data.description) || "");
  const [locationIds, setLocationIds] = useState(
    (_data && [...(_data.locationIds || [])]) || []
  );

  // ── CCO Type ─────────────────────────────────────────────────────────────
  const [ccoType, setCcoType] = useState(() => detectCcoType(_data));

  // ── Bid Item Adjustments ──────────────────────────────────────────────────
  const [ccoBidItems, setCcoBidItems] = useState(() => (_data && _data.ccoBidItems) || []);
  const [selectedBidItemId, setSelectedBidItemId] = useState("");

  // ── Agreed Lump Sum ───────────────────────────────────────────────────────
  const [lumpSumAmount, setLumpSumAmount] = useState(
    () => _data?.ccoAgreedLumpSums?.[0]?.amount ?? ""
  );
  const [lumpSumPaymentClause, setLumpSumPaymentClause] = useState(
    () => _data?.ccoAgreedLumpSums?.[0]?.paymentClause ?? ""
  );

  // ── Force Account ─────────────────────────────────────────────────────────
  const [forceAccountAmount, setForceAccountAmount] = useState(
    () => _data?.ccoExtraWorkForceAccounts?.[0]?.amount ?? ""
  );
  const [forceAccountPaymentClause, setForceAccountPaymentClause] = useState(
    () => _data?.ccoExtraWorkForceAccounts?.[0]?.paymentClause ?? ""
  );

  // Sync all fields when ccoData arrives from Redux (async load)
  useEffect(() => {
    if (!ccoData) return;
    setData({ ...ccoData });
    setCcoNo(ccoData.ccoNo || "");
    setSupplementNo(String(ccoData.supplementNo ?? 0));
    setDescription(ccoData.description || "");
    setLocationIds([...(ccoData.locationIds || [])]);
    setCcoType(ccoData.ccoType || detectCcoType(ccoData));
    setCcoBidItems(ccoData.ccoBidItems || []);
    setLumpSumAmount(ccoData.ccoAgreedLumpSums?.[0]?.amount ?? "");
    setLumpSumPaymentClause(ccoData.ccoAgreedLumpSums?.[0]?.paymentClause ?? "");
    setForceAccountAmount(ccoData.ccoExtraWorkForceAccounts?.[0]?.amount ?? "");
    setForceAccountPaymentClause(ccoData.ccoExtraWorkForceAccounts?.[0]?.paymentClause ?? "");
  }, [ccoData]);

  useEffect(() => {
    const genLoc = (projLocationList || []).find((l) => l.isGeneric);
    if (!genLoc) return;
    if (!locationIds.includes(genLoc.locationId))
      setLocationIds([genLoc.locationId, ...locationIds]);
  }, [projLocationList]);

  const addBidItemRow = () => {
    if (!selectedBidItemId) return;
    const id = parseInt(selectedBidItemId, 10);
    if (ccoBidItems.some((r) => r.bidItemId === id)) return;
    const item = (projItemList || []).find((i) => i.bidItemId === id);
    if (!item) return;
    setCcoBidItems([
      ...ccoBidItems,
      {
        bidItemId: id,
        itemNo: item.itemNo,
        itemShortCaption: item.itemListCaption,
        unitOfMeasure: item.unitOfMeasure,
        eeQuantity: item.eeQuantity || 0,
        ccoQuantity: 0,
      },
    ]);
    setSelectedBidItemId("");
  };

  const updateBidItemCcoQty = (bidItemId, value) => {
    setCcoBidItems(
      ccoBidItems.map((r) =>
        r.bidItemId === bidItemId ? { ...r, ccoQuantity: value } : r
      )
    );
  };

  const removeBidItemRow = (bidItemId) => {
    setCcoBidItems(ccoBidItems.filter((r) => r.bidItemId !== bidItemId));
  };

  const availableBidItems = (projItemList || []).filter(
    (i) => !ccoBidItems.some((r) => r.bidItemId === i.bidItemId)
  );

  // ── Form Validation ───────────────────────────────────────────────────────
  const [formIsIncomplete, setFormIsIncomplete] = useState(false);
  const [formErrors, setFormErrors] = useState({
    ccoNo: null,
    supplementNo: null,
    description: null,
  });

  const validateForm = () => {
    const _formErrors = {
      ...formErrors,
      ccoNo: !ccoNo ? "Required" : null,
      supplementNo: supplementNo !== "" && supplementNo !== null && supplementNo !== undefined && !isNaN(parseInt(supplementNo, 10)) ? null : "Required",
      description: description ? null : "Required",
    };
    setFormErrors(_formErrors);
  };

  useEffect(
    () => setFormIsIncomplete(Object.values(formErrors).some((e) => e)),
    [formErrors]
  );

  useEffect(() => validateForm(), []);
  useEffect(() => validateForm(), [ccoNo, supplementNo, description]);

  const handleBlur = (fieldName, value) => {
    validateForm();
    setData({ ..._data, [fieldName]: value });
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleClick_cancel = (e) => {
    e.preventDefault();
    toggleModal();
  };

  const handleClick_save = async (e) => {
    e.preventDefault();
    if (formIsIncomplete) return;

    const _ccoData = {
      ...(ccoData || {}),
      projectId,
      ccoNo: parseInt(ccoNo, 10) || 0,
      supplementNo: parseInt(supplementNo, 10) || 0,
      description: description,
      locationIds,
      ccoType,
      ccoBidItems:
        ccoType === CCO_TYPES.BID_ITEM
          ? ccoBidItems.map(({ bidItemId, ccoQuantity }) => ({ bidItemId, ccoQuantity }))
          : [],
      ccoAgreedLumpSums:
        ccoType === CCO_TYPES.AGREED_PRICE
          ? [{ amount: parseFloat(lumpSumAmount) || 0, paymentClause: lumpSumPaymentClause }]
          : [],
      ccoExtraWorkForceAccounts:
        ccoType === CCO_TYPES.FORCE_ACCOUNT
          ? [{ amount: parseFloat(forceAccountAmount) || 0, paymentClause: forceAccountPaymentClause }]
          : [],
    };

    let res;
    if (ccoId) res = await dispatch(ccoActions.update(_ccoData));
    else res = await dispatch(ccoActions.add(_ccoData));

    if (res) toggleModal();
  };

  const closeBtn = (
    <button className="close" onClick={toggleModal}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  return (
    <React.Fragment>
      <Modal isOpen={showModal} toggle={toggleModal} size={"xl"} fade={false}>
        <ModalHeader className="bg-info text-white" close={closeBtn}>
          <FontAwesomeIcon icon={ccoId ? "edit" : "plus"} className="fa-fw mr-2" />
          {`${ccoId ? "Edit" : "Add New"} Change Order`}
        </ModalHeader>
        <ModalBody>
          {/* ── Basic Fields ── */}
          <div className="card m-2">
            <div className="card-body p-3">
              <div className="form-row mb-0">
                <div className="form-group mb-0 col-2">
                  <label htmlFor="ccoNo" className="mb-0">
                    <b>CO No.:</b>*
                  </label>
                  <input
                    type="number"
                    name="ccoNo"
                    id="ccoNo"
                    className="form-control"
                    maxLength="3"
                    value={ccoNo}
                    onChange={(e) => setCcoNo(e.target.value)}
                    onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                  />
                  {formErrors.ccoNo && (
                    <span className="error-small">
                      <small>{formErrors.ccoNo}</small>
                    </span>
                  )}
                </div>
                <div className="form-group mb-0 col-2">
                  <label htmlFor="supplementNo" className="mb-0">
                    <b>Supp. No:</b>*
                  </label>
                  <input
                    type="number"
                    name="supplementNo"
                    id="supplementNo"
                    className="form-control"
                    min="0"
                    max="99"
                    value={supplementNo}
                    onChange={(e) => setSupplementNo(e.target.value)}
                    onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                  />
                  {formErrors.supplementNo && (
                    <span className="error-small">
                      <small>{formErrors.supplementNo}</small>
                    </span>
                  )}
                </div>
                <div className="form-group col-8 mb-0">
                  <label htmlFor="description" className="mb-0">
                    <b>Title/Description:</b>*
                  </label>
                  <input
                    type="text"
                    name="description"
                    id="description"
                    className="form-control"
                    maxLength="300"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                  />
                  {formErrors.description && (
                    <span className="error-small">
                      <small>{formErrors.description}</small>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── CCO Type Selection ── */}
          <div className="card m-2">
            <div className="card-header py-2">
              <b>Change Order Type</b>
            </div>
            <div className="card-body p-3">
              <div className="form-row">
                {CCO_TYPE_OPTIONS.map((opt) => (
                  <div key={opt.value} className="col-4">
                    <div
                      className={`card h-100 cursor-pointer${ccoType === opt.value ? " border-info" : ""}`}
                      onClick={() => setCcoType(opt.value)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="card-body p-2">
                        <div className="d-flex align-items-start">
                          <input
                            type="radio"
                            id={`ccoType-${opt.value}`}
                            name="ccoType"
                            value={opt.value}
                            checked={ccoType === opt.value}
                            onChange={() => setCcoType(opt.value)}
                            className="mt-1 mr-2"
                            style={{ flexShrink: 0 }}
                          />
                          <label
                            htmlFor={`ccoType-${opt.value}`}
                            className="mb-0"
                            style={{ cursor: "pointer" }}
                          >
                            <b>{opt.label}</b>
                            <div className="text-muted mt-1" style={{ fontSize: "0.8rem" }}>
                              {opt.description}
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Type-specific sub-form ── */}
              <div className="mt-3">
                {ccoType === CCO_TYPES.BID_ITEM && (
                  <BidItemAdjustmentsSection
                    ccoBidItems={ccoBidItems}
                    availableBidItems={availableBidItems}
                    selectedBidItemId={selectedBidItemId}
                    setSelectedBidItemId={setSelectedBidItemId}
                    onAdd={addBidItemRow}
                    onUpdateQty={updateBidItemCcoQty}
                    onRemove={removeBidItemRow}
                  />
                )}
                {ccoType === CCO_TYPES.FORCE_ACCOUNT && (
                  <ForceAccountSection
                    amount={forceAccountAmount}
                    paymentClause={forceAccountPaymentClause}
                    onAmountChange={setForceAccountAmount}
                    onPaymentClauseChange={setForceAccountPaymentClause}
                  />
                )}
                {ccoType === CCO_TYPES.AGREED_PRICE && (
                  <AgreedLumpSumSection
                    amount={lumpSumAmount}
                    paymentClause={lumpSumPaymentClause}
                    onAmountChange={setLumpSumAmount}
                    onPaymentClauseChange={setLumpSumPaymentClause}
                  />
                )}
              </div>
            </div>
          </div>

          {/* <LocationMultiselect
            projectId={projectId}
            wpType="CO"
            locationIds={locationIds}
            setLocationIds={(locIds) => setLocationIds(locIds)}
          /> */}
        </ModalBody>
        <ModalFooter className="bg-lightgray">
          <div className="form-row form-group mb-0">
            <button
              id="cancel"
              name="cancel"
              className="btn btn-secondary mr-2"
              onClick={handleClick_cancel}
            >
              Cancel
            </button>
            <button
              id="save"
              name="save"
              className="btn btn-primary"
              onClick={handleClick_save}
              disabled={formIsIncomplete}
            >
              <b>{ccoId ? "Save" : "Add"}</b>
            </button>
          </div>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
}

// ── Sub-form: Bid Item Adjustments ────────────────────────────────────────────

function BidItemAdjustmentsSection({
  ccoBidItems,
  availableBidItems,
  selectedBidItemId,
  setSelectedBidItemId,
  onAdd,
  onUpdateQty,
  onRemove,
}) {
  return (
    <div>
      <div className="form-row align-items-center mb-2">
        <div className="col">
          <select
            className="form-control"
            value={selectedBidItemId}
            onChange={(e) => setSelectedBidItemId(e.target.value)}
          >
            <option value="">— Select Bid Item —</option>
            {[...(availableBidItems || [])]
              .sort((a, b) => (a.itemNo || 0) - (b.itemNo || 0))
              .map((item) => (
                <option key={item.bidItemId} value={item.bidItemId}>
                  {item.itemListCaption}
                </option>
              ))}
          </select>
        </div>
        <div className="col-auto">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onAdd}
            disabled={!selectedBidItemId}
          >
            <FontAwesomeIcon icon="plus" className="fa-fw mr-1" />
            Add
          </button>
        </div>
      </div>

      {ccoBidItems.length > 0 ? (
        <table className="table table-sm table-bordered mb-0">
          <thead className="thead-light">
            <tr>
              <th>Item</th>
              <th>UoM</th>
              <th className="text-right">EE Qty</th>
              <th className="text-right" style={{ width: "130px" }}>
                CCO Qty
              </th>
              <th className="text-right">Adj Qty</th>
              <th style={{ width: "40px" }}></th>
            </tr>
          </thead>
          <tbody>
            {[...ccoBidItems].sort((a, b) => (a.itemNo || 0) - (b.itemNo || 0)).map((row) => {
              const ccoQty = parseFloat(row.ccoQuantity) || 0;
              const adjQty = (row.eeQuantity || 0) + ccoQty;
              const adjNegative = adjQty < 0;
              return (
                <tr key={row.bidItemId}>
                  <td>{row.itemShortCaption || row.bidItemCaption}</td>
                  <td>{row.unitOfMeasure || "—"}</td>
                  <td className="text-right">{(row.eeQuantity || 0).toLocaleString()}</td>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm text-right"
                      value={row.ccoQuantity}
                      step="0.001"
                      onChange={(e) => onUpdateQty(row.bidItemId, e.target.value)}
                    />
                  </td>
                  <td className={`text-right${adjNegative ? " text-warning font-weight-bold" : ""}`}>
                    {adjNegative && (
                      <FontAwesomeIcon
                        icon="exclamation-triangle"
                        className="fa-fw mr-1"
                        title="Adjusted quantity would be negative"
                      />
                    )}
                    {adjQty.toLocaleString(undefined, { maximumFractionDigits: 3 })}
                  </td>
                  <td className="text-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0"
                      onClick={() => onRemove(row.bidItemId)}
                      title="Remove"
                    >
                      <FontAwesomeIcon icon="times" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className="text-muted mb-0">
          <small>No bid items added. Select a bid item above and click Add.</small>
        </p>
      )}
    </div>
  );
}

// ── Sub-form: Extra Work at Force Account ─────────────────────────────────────

function ForceAccountSection({ amount, paymentClause, onAmountChange, onPaymentClauseChange }) {
  return (
    <div>
      <div className="alert alert-info mb-2 py-2">
        <FontAwesomeIcon icon="info-circle" className="fa-fw mr-2" />
        The cost for this change order will be determined by tracking labor, equipment, and material.
      </div>
      <div className="form-row">
        <div className="form-group col-3 mb-0">
          <label htmlFor="forceAccountAmount" className="mb-0">
            <b>Estimated Amount ($):</b>
          </label>
          <input
            type="number"
            id="forceAccountAmount"
            className="form-control"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={amount}
            onChange={(e) => onAmountChange(e.target.value)}
          />
        </div>
        <div className="form-group col-9 mb-0">
          <label htmlFor="forceAccountPaymentClause" className="mb-0">
            <b>Payment Clause:</b>
          </label>
          <input
            type="text"
            id="forceAccountPaymentClause"
            className="form-control"
            maxLength="500"
            placeholder="Optional payment clause text"
            value={paymentClause}
            onChange={(e) => onPaymentClauseChange(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

// ── Sub-form: Agreed Lump Sum ─────────────────────────────────────────────────

function AgreedLumpSumSection({ amount, paymentClause, onAmountChange, onPaymentClauseChange }) {
  return (
    <div className="form-row">
      <div className="form-group col-3 mb-0">
        <label htmlFor="lumpSumAmount" className="mb-0">
          <b>Lump Sum Amount ($):</b>
        </label>
        <input
          type="number"
          id="lumpSumAmount"
          className="form-control"
          min="0"
          step="0.01"
          placeholder="0.00"
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
        />
      </div>
      <div className="form-group col-9 mb-0">
        <label htmlFor="lumpSumPaymentClause" className="mb-0">
          <b>Payment Clause:</b>
        </label>
        <input
          type="text"
          id="lumpSumPaymentClause"
          className="form-control"
          maxLength="500"
          placeholder="Optional payment clause text"
          value={paymentClause}
          onChange={(e) => onPaymentClauseChange(e.target.value)}
        />
      </div>
    </div>
  );
}
